import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { SidebarLayout } from '../components/layout';
import { Card } from '../components/layout';
import { DivisionWorkflowManagement } from '../components/divisions/division-workflow-management';
import { useDivisionContext } from '../contexts/division-context';
import { useHelp } from '../contexts/HelpContext';
import { useAuth } from '../hooks/use-auth';
import { Select } from '../components/ui/form';

export function WorkflowsPage() {
  const { divisionId: urlDivisionId } = useParams<{ divisionId?: string }>();
  const navigate = useNavigate();
  const { openHelpPanel } = useHelp();
  const { isAuthenticated, isSupport } = useAuth();
  
  // Use the global division context
  const { selectedDivisionId, setSelectedDivisionId, divisions } = useDivisionContext();

  // Sync selectedDivisionId with URL param
  useEffect(() => {
    if (urlDivisionId && urlDivisionId !== selectedDivisionId) {
      setSelectedDivisionId(urlDivisionId);
    }
  }, [urlDivisionId, selectedDivisionId, setSelectedDivisionId]);

  // Update URL when division selection changes
  const handleDivisionChange = (newDivisionId: string) => {
    setSelectedDivisionId(newDivisionId);
    if (newDivisionId) {
      navigate(`/workflows/${newDivisionId}`);
    } else {
      navigate('/workflows');
    }
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Please log in to view or manage workflows.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Workflow Management</h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage approval workflows by division
            </p>
          </div>
          <button
            onClick={() => openHelpPanel('workflow')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
          >
            <HelpCircle className="h-4 w-4" />
            <span>Help with Workflows</span>
          </button>
        </div>

        {/* Division Selector */}
        <Card className="p-6">
          <div className="max-w-md">
            <Select
              label="Select Division"
              value={selectedDivisionId}
              onChange={(e) => handleDivisionChange(e.target.value)}
            >
              <option value="">Choose a division...</option>
              {divisions.map(division => (
                <option key={division.id} value={division.id}>
                  {division.name} {division.code ? `(${division.code})` : ''}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        {/* Workflow Management Component */}
        {selectedDivisionId ? (
          <DivisionWorkflowManagement 
            divisionId={selectedDivisionId}
            className="mt-6"
            readOnly={isSupport}
          />
        ) : (
          <Card className="p-12">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                Select a Division
              </h2>
              <p className="text-secondary-600 dark:text-secondary-400">
                Choose a division above to manage its workflows
              </p>
            </div>
          </Card>
        )}
      </div>
    </SidebarLayout>
  );
}
